/**
 * A hall's manifest: what the app downloads to show a hall. Every file is content-addressed
 * (its key holds its SHA-256), so a device downloads a file again only when its hash changes,
 * and swapping in a new scan is a new manifest, not new code.
 */

import { checkModel, type MeasuredModel, type Vec2, type Vec3 } from './model';

export interface FileRef {
  /** The name the file had when it was built (Spark asks for chunks by this name). */
  name: string;
  /** Where it lives in the file store. */
  key: string;
  sha256: string;
  bytes: number;
}

export interface SplatSet {
  format: 'rad-chunked';
  /** Spherical-harmonics degree kept: 3 is the scan's full view-dependent colour, 1 the lighter copy. */
  sh: number;
  /** Splats in the scan (the leaves of the level-of-detail tree). */
  splats: number;
  /** Where the splat file's own origin sits in the hall frame (the scan is stored centred). */
  origin: Vec3;
  root: FileRef;
  chunks: FileRef[];
  bytes: number;
}

/**
 * The floor the app draws in place of the scan's: a polished floor mirrors the room, which a scan can only
 * smear. Its tiles are cut from the hall's own photos (tools/make_floor.py); the reflections are drawn live.
 */
export interface DrawnFloor {
  format: 'mawkeb-floor';
  version: 1;
  /** Each a whole tile photographed square-on, veins running as laid; every tile is given one of them. */
  tiles: FileRef[];
  /** Joint to joint, metres, along the grid's x and y. */
  tile: Vec2;
  joint: { width: number; colour: string };
  /** A point where joints cross, in the hall frame, and the grid's turn about z (radians). */
  grid: { origin: Vec2; turn: number };
  /** The share of light mirrored straight down (polished porcelain about 0.05), and how much reflections blur (0 a mirror, 1 matt). */
  finish: { reflectance: number; roughness: number };
  /** The tiles' average colour (sRGB hex), drawn while they load. */
  meanColour: string;
}

/**
 * The hall built from its own photos: its shape measured off its LiDAR scan, every surface covered from the
 * photos, each photo's place first set right against that shape (tools/make_shell.py). The app's Photos view,
 * beside the scan.
 */
export interface PhotoShell {
  format: 'mawkeb-shell';
  version: 1;
  /** The surfaces (a ShellGeometry file). */
  geometry: FileRef;
  /** One texture per surface, in the geometry's order; the light set is half the size, for lighter phones. */
  textures: FileRef[];
  texturesLight: FileRef[];
  /** Texels in the full set, and the bytes of each set. */
  texels: number;
  bytes: number;
  bytesLight: number;
  /** Which run and how many photos it was made from. */
  source: string;
}

/** A PhotoShell's surfaces: each a few triangles in the hall frame with texture coordinates (0 to 1). */
export interface ShellGeometry {
  format: 'mawkeb-shell';
  version: 1;
  surfaces: ShellSurface[];
}

export interface ShellSurface {
  id: string;
  /** wall, end, sill, glass, soffit, ceiling, column, beam, plinth, stage, steps or room. */
  kind: string;
  /** Its texture's size, pixels. */
  size: [number, number];
  positions: number[];
  uv: number[];
  indices: number[];
}

/**
 * How far the walk view keeps from the walls and the columns, metres, for a scan that streaks seen close up
 * (its sides were only photographed from across the hall). Publishing a better scan without it lifts it.
 */
export interface WalkClearance {
  fromWalls: number;
  fromColumns: number;
}

export interface HallManifest {
  format: 'mawkeb-hall-manifest';
  version: 1;
  hall: { id: string; name: string; site?: string };
  /** Shown to people: "Stand-in: synthetic hall", "Scan of 20 October 2026". */
  label: string;
  standIn: boolean;
  published: string;
  model: MeasuredModel;
  /** The scan at full detail. */
  splat: SplatSet | null;
  /** The same scan with less view-dependent colour (about half the size), for lighter phones and Data Saver. */
  splatLight: SplatSet | null;
  /** The floor drawn in place of the scan's, when the hall has one. */
  floor: DrawnFloor | null;
  walkClearance: WalkClearance | null;
  shell: PhotoShell | null;
}

/** The scan a device should load: the lighter copy when asked for and available, else the full one. */
export function pickSplat(m: HallManifest, light: boolean): SplatSet | null {
  return (light && m.splatLight) || m.splat || m.splatLight;
}

/** What the API returns for a hall: the manifest and a short-lived pass to its files. */
export interface HallResponse {
  manifest: HallManifest;
  access: FileAccess;
}

export interface FileAccess {
  /** Signed token proving the holder may read this hall's files until `expires`. */
  token: string;
  expires: string;
  /** URL prefix for files: `${base}${key}?t=${token}`. */
  base: string;
}

export function fileUrl(access: FileAccess, key: string): string {
  return `${access.base}${key.split('/').map(encodeURIComponent).join('/')}?t=${encodeURIComponent(access.token)}`;
}

export class ManifestError extends Error {
  override name = 'ManifestError';
}

const SHA = /^[0-9a-f]{64}$/;

function checkFile(f: FileRef | undefined, where: string): FileRef {
  if (!f || typeof f.key !== 'string' || !f.key || typeof f.name !== 'string' || !SHA.test(f.sha256) || !(f.bytes >= 0)) {
    throw new ManifestError(`manifest: ${where} is incomplete`);
  }
  return f;
}

export function checkManifest(raw: unknown): HallManifest {
  if (!raw || typeof raw !== 'object') throw new ManifestError('manifest: not an object');
  const m = raw as HallManifest;
  if (m.format !== 'mawkeb-hall-manifest') throw new ManifestError(`manifest: unexpected format ${String(m.format)}`);
  if (m.version !== 1) throw new ManifestError(`manifest: version ${String(m.version)} is newer than this app understands`);
  if (!m.hall?.id) throw new ManifestError('manifest: no hall id');
  checkModel(m.model);
  if (m.model.hall.id !== m.hall.id) throw new ManifestError('manifest: the model belongs to another hall');
  m.splatLight ??= null;
  for (const [set, name] of [
    [m.splat, 'splat'],
    [m.splatLight, 'light splat'],
  ] as const) {
    if (!set) continue;
    if (set.format !== 'rad-chunked') throw new ManifestError(`manifest: unknown ${name} format ${String(set.format)}`);
    checkFile(set.root, `the ${name} root`);
    if (!Array.isArray(set.chunks) || set.chunks.length === 0) throw new ManifestError(`manifest: the ${name} has no chunks`);
    set.chunks.forEach((c, i) => checkFile(c, `${name} chunk ${i}`));
  }
  m.floor ??= null;
  if (m.floor) checkFloor(m.floor);
  m.walkClearance ??= null;
  if (m.walkClearance && !(num(m.walkClearance.fromWalls, 0, 5) && num(m.walkClearance.fromColumns, 0, 5))) {
    throw new ManifestError('manifest: the walk clearance must be 0 to 5 m from the walls and from the columns');
  }
  m.shell ??= null;
  if (m.shell) checkShell(m.shell);
  return m;
}

function checkShell(s: PhotoShell) {
  const fail = (msg: string): never => {
    throw new ManifestError(`manifest: the photo hall ${msg}`);
  };
  if (s.format !== 'mawkeb-shell') fail(`has an unknown format ${String(s.format)}`);
  if (s.version !== 1) fail(`is version ${String(s.version)}, newer than this app understands`);
  checkFile(s.geometry, 'the photo hall geometry');
  if (!Array.isArray(s.textures) || s.textures.length === 0) fail('has no textures');
  if (!Array.isArray(s.texturesLight) || s.texturesLight.length !== s.textures.length) fail('light textures don’t match its textures');
  s.textures.forEach((t, i) => checkFile(t, `photo hall texture ${i + 1}`));
  s.texturesLight.forEach((t, i) => checkFile(t, `photo hall light texture ${i + 1}`));
  if (!num(s.texels, 1, 1e9) || !num(s.bytes, 0, 1e10) || !num(s.bytesLight, 0, 1e10)) fail('sizes are missing');
}

/** A photo hall's geometry as fetched, checked against the textures it was published with. */
export function checkShellGeometry(raw: unknown, textures: number): ShellGeometry {
  const fail = (msg: string): never => {
    throw new ManifestError(`photo hall geometry: ${msg}`);
  };
  const g = raw as ShellGeometry;
  if (!g || g.format !== 'mawkeb-shell' || g.version !== 1) fail('not a version 1 photo hall');
  if (!Array.isArray(g.surfaces) || g.surfaces.length !== textures) fail(`has ${g.surfaces?.length ?? 0} surfaces for ${textures} textures`);
  g.surfaces.forEach((s, i) => {
    const n = Array.isArray(s.positions) ? s.positions.length / 3 : 0;
    if (!n || !Number.isInteger(n) || !s.positions.every((v) => num(v, -1e4, 1e4))) fail(`surface ${i + 1} has no corners`);
    if (!Array.isArray(s.uv) || s.uv.length !== 2 * n || !s.uv.every((v) => num(v, -0.01, 1.01))) fail(`surface ${i + 1}’s texture coordinates don’t fit`);
    if (!Array.isArray(s.indices) || s.indices.length % 3 !== 0 || !s.indices.every((v) => Number.isInteger(v) && v >= 0 && v < n)) {
      fail(`surface ${i + 1}’s triangles don’t fit`);
    }
  });
  return g;
}

const HEX = /^#[0-9a-f]{6}$/i;
const num = (v: unknown, lo: number, hi: number) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

function checkFloor(f: DrawnFloor) {
  const fail = (msg: string): never => {
    throw new ManifestError(`manifest: the drawn floor ${msg}`);
  };
  if (f.format !== 'mawkeb-floor') fail(`has an unknown format ${String(f.format)}`);
  if (f.version !== 1) fail(`is version ${String(f.version)}, newer than this app understands`);
  if (!Array.isArray(f.tiles) || f.tiles.length === 0) fail('has no tiles');
  if (f.tiles.length > 16) fail(`has ${f.tiles.length} tiles; 16 at most`);
  f.tiles.forEach((t, i) => checkFile(t, `floor tile ${i + 1}`));
  if (!Array.isArray(f.tile) || !f.tile.every((v) => num(v, 0.05, 5))) fail('tile size is missing or out of range');
  if (!num(f.joint?.width, 0, 0.05) || !HEX.test(f.joint?.colour ?? '')) fail('joint is incomplete');
  if (!Array.isArray(f.grid?.origin) || f.grid.origin.length !== 2 || !f.grid.origin.every((v) => num(v, -1e4, 1e4)) || !num(f.grid.turn, -7, 7)) {
    fail('grid is incomplete');
  }
  if (!num(f.finish?.reflectance, 0, 1) || !num(f.finish?.roughness, 0, 1)) fail('finish is incomplete');
  if (!HEX.test(f.meanColour ?? '')) fail('average colour is missing');
}

/** Every file of one scan, root first. */
export function setFiles(set: SplatSet | null): FileRef[] {
  return set ? [set.root, ...set.chunks] : [];
}
